/** 「解决方案」页内容：按使用者的处境讲，而不是按功能讲。 */
import type { IconName } from './icons';
import type { ActionLink } from './home';

export interface SolutionsIntro {
  eyebrow: string;
  title: string;
  lead: string;
  actions: readonly ActionLink[];
}

export const SOLUTIONS_INTRO: SolutionsIntro = {
  eyebrow: '解决方案',
  title: '同一套平台，五种落地方式',
  lead: '低空运营的参与者差异显著：既有园区管理方，也有作业团队、科研机构，以及单人单机的开发者。平台不预设您属于哪一类，只假设您需要把机体管起来。',
  actions: [
    { label: '查看方案与定价', to: '/pricing', variant: 'primary' },
    { label: '直接联系', to: '/contact', variant: 'secondary' },
  ],
};

export interface Scenario {
  id: string;
  icon: IconName;
  title: string;
  audience: string;
  tagline: string;
  situation: string;
  approach: readonly string[];
  outcomes: readonly string[];
}

export const SCENARIOS: readonly Scenario[] = [
  {
    id: 'park',
    icon: 'pin',
    title: '园区与起降场监管',
    audience: '园区运营方、通航机场、起降点管理单位',
    tagline: '在同一张图上掌握各单位的机体动态。',
    situation:
      '同一片空域内往往有不止一家单位执行飞行任务，而各方系统彼此隔离。一旦发生情况需逐一致电确认，事后亦难以还原当时在场机体的完整情况。',
    approach: [
      '将各单位的机体登记至同一份设备档案，凭据相互隔离，视图统一呈现',
      '总览页呈现全部位置与航迹，异常机体按告警等级自动置顶',
      '为管理岗位分配观察员角色，仅可查看不可下令，避免权限外溢',
      '关键操作全部写入审计日志，事后可按时间回溯',
    ],
    outcomes: ['在场机体情况一目了然', '异常主动推送至值班人员', '责任链条有据可查'],
  },
  {
    id: 'inspection',
    icon: 'route',
    title: '巡检作业队',
    audience: '电力、水利、管线、市政巡检团队',
    tagline: '多机并行作业时，第一时间定位异常机体。',
    situation:
      '数台机体同时作业，操作员需持续盯守同一块屏幕，最容易遗漏的正是某台机体电量与链路持续下滑的那数十秒。',
    approach: [
      '单机管理页将姿态、链路与关键指标以大字号呈现，便于快速判断',
      '电量按三级阈值提前告警，链路质量以分级呈现而非持续跳动的小数',
      '起飞与降落前执行条件检查清单，不可逆操作单独标识',
      '全屏模式供值班人员整屏值守，无需在菜单间反复切换',
    ],
    outcomes: ['缩短漏判窗口', '异常处置有固定顺序', '值班岗与飞手职责分明'],
  },
  {
    id: 'research',
    icon: 'graduation',
    title: '科研与教学',
    audience: '高校实验室、职教实训、科研院所',
    tagline: '无需消耗真机，实操环节亦可完整覆盖。',
    situation:
      '教学与算法验证需反复运行完整链路，但真机成本高、场地难约、失败代价大，导致大量环节只能停留于理论讲解。',
    approach: [
      '内置 6 台模拟机体，注册、总览、单机管理、指令与告警流程均可完整运行',
      '可主动注入断链、慢响应与随机失败，用于验证降级与容错逻辑',
      '接口文档公开，学生可自行实现一台「机体」接入平台',
      '审计日志可复盘每一次指令，适合作为实验过程记录',
    ],
    outcomes: ['实操不受场地限制', '失败场景可复现、可对比', '课堂所用即真实接口'],
  },
  {
    id: 'diy',
    icon: 'cpu',
    title: '个人开发者与 DIY',
    audience: '自研机体、开源飞控改机、独立开发者',
    tagline: '自研机体同样具备企业级监管能力。',
    situation:
      '个人开发的机体通常仅在本机运行地面站软件。若需让他人查看、留存记录或多人协作，则不得不自行从零构建一套后台。',
    approach: [
      '按公开文档实现接口，将端点填入设备档案即可纳入平台监管',
      '三种凭据模式可按实际能力选择，长期密钥与短期令牌均非强制',
      '既可接入单台机体，也可组建小型机队',
      '平台支持自部署，数据留存于贵方自有账号',
    ],
    outcomes: ['无需自行开发后台', '具备企业机队同等的监管能力', '可随时迁出，数据自主持有'],
  },
  {
    id: 'enterprise',
    icon: 'building',
    title: '企业定制无人机 SaaS',
    audience: '需要自有品牌与自有流程的运营企业、集成商',
    tagline: '改造为契合贵方运营方式的形态。',
    situation:
      '通用后台往往在角色划分、审批流程与命名习惯上难以契合，最终要么业务迁就软件，要么在系统之外另建一层表格。',
    approach: [
      '私有化部署至贵方 Cloudflare 账号与域名，账号体系由贵方管理',
      '角色、权限点与界面命名依贵方组织方式调整',
      '设备档案、遥测与审计可按接口对接至既有业务系统',
      '从机体到平台是完整路径，如有需要可将配套软硬件一并商议',
    ],
    outcomes: ['流程与系统相互契合', '数据与账号完全自主掌控', '支持分阶段逐步扩展'],
  },
];

export interface FitItem {
  text: string;
}

export const FIT_WELL: readonly string[] = [
  '已持有多台机体，或预计规模将持续扩大',
  '需要向他人证明「彼时由谁执飞、飞行于何处」',
  '机体为自研或第三方设备，只要可实现一份接口',
  '希望账号、数据与部署位置均由自身掌控',
];

export const FIT_NOT_WELL: readonly string[] = [
  '仅需采购无人机整机 —— 我们不销售整机',
  '所需的是航测建模、图像拼接一类的数据处理软件',
  '机体完全无法通过网络访问，亦无可用代理方案',
  '期望开箱即用，不接受任何接入工作',
];

export interface Narrative {
  eyebrow: string;
  title: string;
  paragraphs: readonly string[];
  points: readonly { icon: IconName; title: string; text: string }[];
}

export const NARRATIVE: Narrative = {
  eyebrow: '为什么做这件事',
  title: '在讨论规模之前，先把底座做扎实',
  paragraphs: [
    '低空经济常被赋予宏大的想象，但真正落地时，最先受制约的往往不是飞行能力，而是「管」：谁在飞、飞得如何、事后如何查证。这些环节若不够扎实，机队规模一旦上升便会失序。',
    '因此 Havorix™ 从一台自研机体起步，逐步向上构建监管平台与配套软硬件。我们并不追求功能数量，只坚持三件事：链路清楚、状态诚实、权限分明。',
  ],
  points: [
    { icon: 'shield', title: '安全可靠', text: '链路异常即如实呈现。权限判断仅在服务端执行一次，不依赖使用者的自觉。' },
    { icon: 'sliders', title: '操作简易', text: '高频操作一屏完成，危险动作单独标识并要求确认。' },
    { icon: 'scale', title: '详略得当', text: '总览仅呈现判断所需的数据，细节保留给真正需要它的角色。' },
  ],
};
